import type { Client } from './client';
import { createRequestOptions } from './request-options';
import { genExtRes } from '../utils/common';

/**
 * Проверить передаются ли файлы
 */
const checkHasFiles = (data: Record<string, any>): boolean => {
	return Object.values(data).some(value =>
		value instanceof FileList ||
		value instanceof File ||
		value instanceof Blob ||
		Array.isArray(value) && value.some(item => item instanceof File || item instanceof Blob),
	);
};

export const createRequest = <
	RequestContext extends Api.ClientRequestContext<any, any, any>,
	FieldsNarrow extends Api.ParamsFields<RequestContext>,
>(
	client: Client<RequestContext['Paths'], RequestContext['UseExt']>,
	path: RequestContext['Path'],
	fieldsNarrow?: FieldsNarrow,
): Api.ClientRequest<RequestContext, FieldsNarrow> => {
	const clientRequest: Api.ClientRequest<RequestContext, FieldsNarrow> = {
		inLoading: false,
		path: path as Api.PathAbstract,
		url: client.options.baseUrl + path,
		options: createRequestOptions(client.options.requestOptions),
		mockerOptions: undefined as Api.Mocker.Options<any, any> | undefined,
		params: {} as any, // незаполненное свойство
		getInLoading: () => clientRequest.inLoading,

		setParams: (params) => {
			clientRequest.params = { ...params } as any;

			return clientRequest;
		},

		changeParams: (params) => {
			Object.assign(clientRequest.params, params);

			return clientRequest;
		},

		setOptions: (options) => {
			Object.assign(clientRequest.options, options);

			return clientRequest;
		},

		setMocker: (options) => {
			clientRequest.mockerOptions = options;

			return clientRequest;
		},

		call: async (fields?) => {
			return clientRequest.exec(fields);
		},

		callFetch: async (fetchStyle, fields?) => {
			return clientRequest.exec(fields, fetchStyle);
		},

		callInNewWindow: async (fields?) => {
			const data = clientRequest.genData(fields);

			const middlewareCtx = client.genMiddlewareCtx(clientRequest, data);

			client.options.onPrepareData?.(data, middlewareCtx);

			const { postInWindow } = await import('../utils/lazy');
			void postInWindow(clientRequest.url, data, '_blank');
		},

		callInSelfWindow: async (fields?) => {
			const data = clientRequest.genData(fields);

			const middlewareCtx = client.genMiddlewareCtx(clientRequest, data);

			client.options.onPrepareData?.(data, middlewareCtx);

			const { postInWindow } = await import('../utils/lazy');
			void postInWindow(clientRequest.url, data, '_self');
		},

		genData: (fields?, fetchStyle?) => {
			const post: any = { ...clientRequest.params };

			// в старом коде fields вставлялись в params, оставлено для совместимости
			// delete post.fields;
			delete post.fetch_style;

			// поля, указанные при запуске запроса
			if (fields) {
				let fieldsPrepared = [...fields as any[]];

				// поля, указанные при создании запроса
				if (fieldsNarrow) {
					fieldsPrepared = fieldsPrepared.filter(field => fieldsNarrow.includes(field));
				}

				post.fields = fieldsPrepared;
			} else {
				if (fieldsNarrow) {
					post.fields = fieldsNarrow;
				}
			}

			if (fetchStyle) {
				post.fetch_style = fetchStyle;
			}

			return post;
		},

		abortByFingerprint: () => {
			if (!clientRequest.options.checkFingerprint) return;

			const fingerprint = clientRequest.path + clientRequest.options.checkFingerprint;

			client.abortControllers[fingerprint]?.abort(
				new DOMException(`Stopped previous request by fingerprint: ${clientRequest.options.checkFingerprint}`, 'AbortError'),
			);

			const controller = new AbortController();

			client.abortControllers[fingerprint] = controller;

			return controller.signal;
		},

		/**
		 * Общая функция выполнения запросов
		 */
		async exec(fields?, fetchStyle?) {
			const Request = client.options.Request;
			const fetch = client.options.fetch;

			const data = clientRequest.genData(fields, fetchStyle);

			const middlewareCtxWithoutRequest = client.genMiddlewareCtx(clientRequest, data);

			client.options.onPrepareData?.(data, middlewareCtxWithoutRequest);

			const hasFiles = checkHasFiles(data);
			if (hasFiles && clientRequest.options.method !== 'POST') {
				throw new Error('Files can be sent only in POST requests');
			}

			let url = clientRequest.url;

			let requestInitBody: string | FormData | undefined = undefined;

			if (hasFiles) {
				if (clientRequest.options.method !== 'POST') {
					throw new Error('Files can be sent only in POST requests');
				}

				const { formDataSerializer } = await import('../utils/lazy');
				requestInitBody = formDataSerializer(data);
			} else if (clientRequest.options.method !== 'POST') {
				url += '?' + await client.options.querySerializer(data);
			} else {
				requestInitBody = client.options.bodySerializer(data);
			}

			// checkFingerprint
			let requestInitSignal = clientRequest.abortByFingerprint();

			if (clientRequest.mockerOptions) {
				const { genMockResponse } = await import('../mocker/');

				return await genMockResponse(clientRequest.mockerOptions, fields, data, requestInitSignal);
			}

			const requestInit: RequestInit = {
				...client.options,
				method: clientRequest.options.method,
				body: requestInitBody,
				signal: requestInitSignal,
			};

			let request = new Request(url, requestInit);

			request.headers.set('x-request-id', middlewareCtxWithoutRequest.id);
			request.headers.set('accept', clientRequest.options.accept);

			// при отправке данных в `FormData` `fetch()` сам укажет правильный заголовок `multipart/form-data`
			if (!hasFiles) {
				request.headers.set('content-type', 'application/json');
			}

			const middlewareCtx: Api.MiddlewareCallbackParams = {
				...middlewareCtxWithoutRequest,
				request,
			};

			clientRequest.inLoading = true;
			clientRequest.options.onLoading?.(clientRequest.inLoading, middlewareCtx);

			let response: Response | undefined = undefined;

			const requestOrResponse = await client.middlewareOnRequest(request, middlewareCtx);

			if (requestOrResponse instanceof Response) {
				response = requestOrResponse;
			} else if (requestOrResponse instanceof Request) {
				request = requestOrResponse;
			}

			if (!response) {
				try {
					response = await fetch(request);
				} catch (e) {
					response = await client.middlewareOnError(e, middlewareCtx);
				} finally {
					clientRequest.inLoading = false;
					clientRequest.options.onLoading?.(clientRequest.inLoading, middlewareCtx);
				}
			}

			response = await client.middlewareOnResponse(response, middlewareCtx);

			let res: any;
			let responseText: string | undefined;
			let formatError: undefined | Api.FormatError = undefined;

			try {
				// json - стандартные формат, он может быть возвращен в запросах с другим responseType, если в них произошла ошибка
				const isJSON = response.headers.get('content-type')?.includes('application/json');

				responseText = await response.text();

				if (this.options.accept === 'application/json' || isJSON) {
					res = JSON.parse(responseText);
				} else {
					if (client.useExt) {
						res = genExtRes(responseText, undefined, response);
					} else {
						res = responseText;
					}
				}
			} catch (e) {
				if (e instanceof SyntaxError || e instanceof TypeError) {
					formatError = e;
					formatError.responseText = responseText;
				}

				// если в результате не пришел json, сгенерировать объект результата и добавить информацию об ответе API
				if (client.useExt && formatError) {
					let errorCode = response.status;
					let errorMessage = response.statusText;

					if (errorCode === 200) {
						errorCode = 10000;
						errorMessage = 'Error API response';
					}

					res = genExtRes(null, [
						{
							code: errorCode,
							string: errorMessage,
						},
					], response);

					if (client.options.useApiConsole) console.error(e);
				} else {
					throw e;
				}
			}

			if (client.useExt) {
				res.response = response;

				if (res) {
					clientRequest.options.onApiLoaded?.(res, middlewareCtx);
				}

				if (res?.errors) {
					if (client.options.useApiConsole) console.warn(new Error('API error'), { res });

					clientRequest.options.onApiError?.(res, { ...middlewareCtx, formatError }, client.options.requestOptions?.onApiError);
				}

				if (res?.messages) {
					clientRequest.options.onApiMessage?.(res, middlewareCtx);
				}
			}

			return res;
		},
	};

	return clientRequest;
};
